import * as styles from "./DateRecurrencePicker.css";

import { clsx } from "clsx";
import { type JSX, useCallback, useMemo, useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@frontend/components/atoms/Popover";
import { useControlContext } from "@frontend/contexts";
import { useLingui } from "@lingui/react/macro";

import { DateRecurrencePanel } from "./DateRecurrencePanel";
import type { DateRecurrencePickerProps } from "./DateRecurrencePicker.types";
import { describeRecurrence } from "./recurrenceRule";
import { recurrenceLanguage, useRecurrenceGetText } from "./recurrenceText";

export function DateRecurrencePicker({
  value,
  onValueChange,
  exceptions = [],
  onExceptionsChange,
  startDate,
  onStartDateChange,
  showEnd = false,
  tour = true,
  onCopy,
  locale,
  disabled = false,
  variant = "default",
  size,
  placeholder,
  className,
}: DateRecurrencePickerProps): JSX.Element {
  const { t } = useLingui();

  const inherited = useControlContext();

  const resolvedSize = size ?? inherited.size;

  const gettext = useRecurrenceGetText();

  const language = useMemo(() => recurrenceLanguage(locale), [locale]);

  const description = describeRecurrence(value ?? "", gettext, language);

  const empty = placeholder ?? t`No recurrence`;

  const label = description ?? empty;

  const count = exceptions.length;

  const [open, setOpen] = useState(false);

  const [touring, setTouring] = useState(false);

  const [side, setSide] = useState<"top" | "bottom">("bottom");

  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const frozen = useRef<{ left: number; width: number } | null>(null);

  /**
   * The trigger reads the rule back as a sentence, so writing the rule rewrites
   * it, and a trigger that fits its text changes width under the popup. The
   * popup keeps the horizontal place it opened at, and follows the trigger for
   * everything else: scrolling, and the height the panel takes.
   */
  const anchor = useCallback(() => {
    const trigger = triggerRef.current;

    return {
      contextElement: trigger ?? undefined,
      getBoundingClientRect: (): DOMRect => {
        const rect = trigger?.getBoundingClientRect() ?? new DOMRect();
        const held = frozen.current;

        return null === held
          ? rect
          : new DOMRect(held.left, rect.top, held.width, rect.height);
      },
    };
  }, []);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          const rect = triggerRef.current?.getBoundingClientRect();

          if (rect) {
            frozen.current = { left: rect.left, width: rect.width };

            // The panel is as tall as the tab it shows, so it opens on the side
            // that has the most room and stays there: a side changing under the
            // hand writing the rule would read as a different popup.
            setSide(
              rect.top > window.innerHeight - rect.bottom ? "top" : "bottom",
            );
          }
        } else {
          frozen.current = null;
        }

        if (next || !touring) {
          setOpen(next);
        }
      }}
    >
      {variant === "link" ? (
        <PopoverTrigger
          ref={triggerRef}
          render={
            <Button
              type="button"
              variant="link"
              size="sm"
              disabled={disabled}
              className={className}
              data-slot="date-recurrence-picker"
              data-variant="link"
              leftSection={<Icon name="Repeat" size="sm" />}
              label={
                count > 0 ? `${label} (${t`${count} exception(s)`})` : label
              }
            />
          }
        />
      ) : (
        <PopoverTrigger
          ref={triggerRef}
          type="button"
          disabled={disabled}
          data-slot="date-recurrence-picker"
          data-variant="default"
          className={clsx(styles.trigger({ size: resolvedSize }), className)}
        >
          <span
            className={clsx(styles.value, !description && styles.placeholder)}
          >
            {label}
          </span>

          {count > 0 ? (
            <span className={styles.count}>{t`${count} exception(s)`}</span>
          ) : null}

          <span className={styles.icon} aria-hidden="true">
            <Icon name="Repeat" />
          </span>
        </PopoverTrigger>
      )}

      <PopoverContent
        anchor={anchor}
        side={side}
        align="start"
        // The side is settled when the popup opens, so a panel that grows
        // stops at the room left and scrolls rather than changing side.
        collisionAvoidance={{ side: "none", align: "shift" }}
        className={styles.popup}
      >
        <DateRecurrencePanel
          scrollable
          tour={tour}
          onTourRunningChange={setTouring}
          onCopy={onCopy}
          framed={false}
          heading={<PopoverTitle>{t`Recurrence`}</PopoverTitle>}
          value={value}
          onValueChange={onValueChange}
          exceptions={exceptions}
          onExceptionsChange={onExceptionsChange}
          startDate={startDate}
          onStartDateChange={onStartDateChange}
          showEnd={showEnd}
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
}
